/**
 * Listings API Client
 *
 * Unified class-based API for both public and partner listings endpoints.
 * All endpoints now use composed structure with LocalizedText (snake_case).
 *
 * Breaking changes from legacy:
 * - Partner endpoints return same composed structure as public endpoints
 * - CreateListingRequest/UpdateListingRequest use composed blocks
 * - All fields are snake_case
 * - LocalizedText required for all user-facing strings
 */

import { apiClient, ApiClient } from "@/shared/api/baseClient";
import type {
  ListingsSearchParams,
  ListingsSearchResponse,
  Listing,
} from "@/shared/types/listings";
import {
  transformListing,
  transformListings,
  buildListingsQueryString,
  type ApiListingResponse,
} from "@/shared/lib/transformers/listings";
import type {
  LocalizedText,
  Currency,
  GeoPoint,
  Image,
} from "@/shared/types/primitives";

// ============================================
// Enums
// ============================================

/**
 * Listing publication lifecycle status
 */
export enum PublicationStatus {
  DRAFT = "draft",
  PUBLISHED = "published",
  EXPIRED = "expired",
}

// ============================================
// Request DTOs (Composed Structure)
// ============================================

/**
 * Station input for create/update operations
 */
export interface StationInfoInput {
  name: LocalizedText;
  walk_minutes: number;
  distance_m?: number;
  line_name?: LocalizedText;
  line_code?: string;
  line_color?: string;
}

/**
 * Location block input
 */
export interface LocationBlockInput {
  prefecture: LocalizedText;
  city: LocalizedText;
  ward?: LocalizedText;
  address_display: LocalizedText;
  stations: StationInfoInput[];
  coordinates?: GeoPoint;
}

/**
 * Financials block input
 */
export interface FinancialsBlockInput {
  rent: Currency;
  management_fee?: Currency;
  deposit?: Currency;
  key_money?: Currency;
  guarantor_required: boolean;
  renewal_fee?: Currency;
  insurance_fee?: Currency;
}

/**
 * Property details block input
 */
export interface PropertyDetailsBlockInput {
  property_type: string;
  structure?: LocalizedText;
  room_layout: string;
  area_sqm: number;
  balcony_area_sqm?: number;
  floor?: number;
  total_floors?: number;
  year_built?: number;
}

/**
 * Amenities block input (flat structure for convenience)
 */
export interface AmenitiesBlockInput {
  // Bathroom
  bathroom_separate?: boolean;
  bathroom_dryer?: boolean;
  bathroom_washlet?: boolean;
  bathroom_reheating?: boolean;
  bathroom_independent_vanity?: boolean;
  // Kitchen
  kitchen_system?: boolean;
  kitchen_dishwasher?: boolean;
  kitchen_gas_stove?: boolean;
  kitchen_ih_cooktop?: boolean;
  kitchen_counter?: boolean;
  // Security
  security_auto_lock?: boolean;
  security_video_intercom?: boolean;
  security_delivery_box?: boolean;
  security_camera?: boolean;
  // Contract
  contract_pets_allowed?: boolean;
  contract_foreigner_friendly?: boolean;
  contract_instruments_ok?: boolean;
  contract_soho_allowed?: boolean;
  // Comfort
  comfort_air_conditioning?: boolean;
  comfort_flooring_wood?: boolean;
  comfort_south_facing?: boolean;
  comfort_corner_unit?: boolean;
  // Connectivity
  connectivity_internet_included?: boolean;
  // Parking
  parking_car?: boolean;
  parking_bicycle?: boolean;
  parking_motorcycle?: boolean;
  // Laundry
  laundry_washer_hookup?: boolean;
  laundry_washer_dryer_in_unit?: boolean;
  laundry_indoor_washer_space?: boolean;
  // Building
  building_elevator?: boolean;
  building_balcony?: boolean;
}

/**
 * Media block input
 */
export interface MediaBlockInput {
  images_by_section: Record<string, Image[]>;
}

/**
 * Create listing request with composed structure
 */
export interface CreateListingRequest {
  // Status (optional on create)
  publication_status?: string;
  availability_status?: string;
  available_from?: string;

  // Source
  source?: string;
  source_url?: string;

  // Composed blocks (required)
  title: LocalizedText;
  description: LocalizedText;
  location: LocationBlockInput;
  financials: FinancialsBlockInput;
  property: PropertyDetailsBlockInput;
  amenities?: AmenitiesBlockInput;
  media?: MediaBlockInput;

  // Metadata
  metadata?: Record<string, unknown>;
}

/**
 * Update listing request with composed structure (all fields optional)
 */
export interface UpdateListingRequest {
  // Status
  publication_status?: string;
  availability_status?: string;
  available_from?: string;

  // Source
  source?: string;
  source_url?: string;

  // Composed blocks (all optional for partial update)
  title?: LocalizedText;
  description?: LocalizedText;
  location?: LocationBlockInput;
  financials?: FinancialsBlockInput;
  property?: PropertyDetailsBlockInput;
  amenities?: AmenitiesBlockInput;
  media?: MediaBlockInput;

  // Metadata
  metadata?: Record<string, unknown>;
}

// ============================================
// Response DTOs
// ============================================

/**
 * Partner listings search response (same structure as public)
 */
export interface PartnerListingsSearchResponse {
  listings: Listing[];
  total_count: number;
  next_cursor?: string;
  has_more: boolean;
}

/**
 * Create listing response
 */
export interface CreateListingResponse {
  listing: Listing;
}

/**
 * Update listing response
 */
export interface UpdateListingResponse {
  listing: Listing;
}

/**
 * Delete listing response
 */
export interface DeleteListingResponse {
  ok: boolean;
}

// ============================================
// Backend API Response Types (Internal)
// ============================================

interface BackendListingsResponse {
  listings: ApiListingResponse[];
  total_count: number;
  next_cursor?: string | null;
  has_more: boolean;
}

interface BackendListingResponse {
  listing: ApiListingResponse;
}

interface BackendCreateListingResponse {
  listing: ApiListingResponse;
}

interface BackendUpdateListingResponse {
  listing: ApiListingResponse;
}

// ============================================
// Listings API Class
// ============================================

/**
 * Unified Listings API Client
 *
 * Handles both public endpoints (search/browse) and partner endpoints (CRUD).
 * ALL endpoints now return composed Listing structure with LocalizedText.
 *
 * @example
 * ```ts
 * const api = new ListingsApi(apiClient);
 *
 * // Public: Search listings (no auth)
 * const results = await api.searchListings({ prefecture: "tokyo" });
 *
 * // Partner: Fetch my listings (requires auth)
 * const myListings = await api.fetchPartnerListings(token);
 *
 * // Partner: Create listing with composed structure
 * const newListing = await api.createListing(token, {
 *   title: { ja: "タイトル", en: "Title", ru: "Заголовок" },
 *   // ... other composed blocks
 * });
 * ```
 */
export class ListingsApi {
  constructor(private client: ApiClient) {}

  // ==========================================
  // PUBLIC Endpoints (No Auth Required)
  // ==========================================

  /**
   * Search published listings with filters (PUBLIC - no auth)
   */
  async searchListings(
    params: ListingsSearchParams,
  ): Promise<ListingsSearchResponse> {
    const queryString = buildListingsQueryString(params);
    const endpoint = queryString ? `/listings?${queryString}` : "/listings";
    const response = await apiClient.get<BackendListingsResponse>(endpoint);

    return {
      listings: transformListings(response.listings),
      total_count: response.total_count,
      next_cursor: response.next_cursor ?? undefined,
      has_more: response.has_more,
    };
  }

  /**
   * Get a single published listing by ID (PUBLIC - no auth)
   */
  async getListing(id: string): Promise<Listing> {
    const encodedId = encodeURIComponent(id);
    const response = await apiClient.get<BackendListingResponse>(
      `/listings/${encodedId}`,
    );
    return transformListing(response.listing);
  }

  /**
   * Load more published listings with pagination (PUBLIC - no auth)
   */
  async loadMoreListings(
    params: ListingsSearchParams,
    cursor: string,
  ): Promise<ListingsSearchResponse> {
    return this.searchListings({ ...params, cursor });
  }

  // ==========================================
  // PARTNER Endpoints (Auth Required)
  // ==========================================

  /**
   * Fetch all listings for authenticated partner organization (PARTNER - auth required)
   * Returns composed Listing structure (same as public endpoints)
   */
  async fetchPartnerListings(
    token: string,
  ): Promise<PartnerListingsSearchResponse> {
    this.client.setAuthToken(token);
    const response =
      await this.client.get<BackendListingsResponse>("/listings/my");

    return {
      listings: transformListings(response.listings),
      total_count: response.total_count,
      next_cursor: response.next_cursor ?? undefined,
      has_more: response.has_more,
    };
  }

  /**
   * Fetch single listing by ID for editing (PARTNER - auth required)
   * Returns composed Listing structure
   */
  async fetchPartnerListing(
    token: string,
    listingId: string,
  ): Promise<Listing> {
    this.client.setAuthToken(token);
    const response = await this.client.get<BackendListingResponse>(
      `/listings/my/${listingId}`,
    );
    return transformListing(response.listing);
  }

  /**
   * Create a new listing (PARTNER - auth required)
   * Accepts and returns composed Listing structure
   */
  async createListing(
    token: string,
    data: CreateListingRequest,
  ): Promise<Listing> {
    this.client.setAuthToken(token);
    const response = await this.client.post<BackendCreateListingResponse>(
      "/listings",
      data,
    );
    return transformListing(response.listing);
  }

  /**
   * Update an existing listing with PATCH (PARTNER - auth required)
   * Accepts and returns composed Listing structure
   */
  async updateListing(
    token: string,
    listingId: string,
    data: UpdateListingRequest,
  ): Promise<Listing> {
    this.client.setAuthToken(token);
    const response = await this.client.patch<BackendUpdateListingResponse>(
      `/listings/${listingId}`,
      data,
    );
    return transformListing(response.listing);
  }

  /**
   * Delete a listing (PARTNER - auth required)
   */
  async deleteListing(
    token: string,
    listingId: string,
  ): Promise<DeleteListingResponse> {
    this.client.setAuthToken(token);
    return this.client.delete<DeleteListingResponse>(`/listings/${listingId}`);
  }
}
